import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapPatientSummaries } from '@/api/mappers';
import type { PatientSummaryViewModel } from '@/models/patient';

export const RECENT_PATIENTS_DEFAULT_LIMIT = 50;
export const RECENT_PATIENTS_PAGE_SIZE = 50;
/**
 * Ceiling the live endpoint enforces on `limit` — requests above this are
 * rejected with `validation_failed` ("must be less than or equal to 200").
 * Progressive loading clamps to this, so the browse list tops out at 200 rows.
 */
export const RECENT_PATIENTS_MAX_LIMIT = 200;

export interface UseRecentPatientsOptions {
  readonly limit?: number;
}

/**
 * Patients the current user has recently accessed, mapped to the UI
 * view-model shape. Wraps `GET /recent-patients` with a stable query key
 * so filters or invalidations from elsewhere (e.g., mutation flows) can
 * refresh the browse landing without each caller re-deriving the key.
 *
 * Pagination: the live endpoint exposes only `limit` (no offset/cursor),
 * so progressive loading bumps `limit` and re-issues the GET. Each
 * response includes the prior rows. `placeholderData: keepPreviousData`
 * keeps the previous list visible while the bigger fetch is in flight,
 * so the UI doesn't flash a skeleton on every Load-more click.
 */
export function useRecentPatients(
  options: UseRecentPatientsOptions = {},
): UseQueryResult<readonly PatientSummaryViewModel[]> {
  const limit = Math.min(
    options.limit ?? RECENT_PATIENTS_DEFAULT_LIMIT,
    RECENT_PATIENTS_MAX_LIMIT,
  );
  return useQuery<readonly PatientSummaryViewModel[]>({
    queryKey: ["recent-patients", limit] as const,
    queryFn: async ({ signal }) => {
      const dtos = await apiClient.getRecentPatients({ limit }, { signal });
      return mapPatientSummaries(dtos);
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}
